import TextRecognition from '@react-native-ml-kit/text-recognition';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AppState, Pressable, StatusBar, StyleSheet, Text, TextInput, View } from 'react-native';
import {
  Camera,
  useCameraDevice,
  useCameraPermission,
  useCodeScanner,
} from 'react-native-vision-camera';
import { Glyph, TICK } from '~/components/decision';
import { Screen } from '~/components/Screen';
import { Card, Notice, OutlineButton, PrimaryButton } from '~/components/ui';
import { logger } from '~/lib/logger';
import { extractVin, normalizeVin, type VinCandidate, vinFromLicenceDisc } from '~/lib/vin';
import { useTheme } from '~/theme/ThemeProvider';

/**
 * Step 1 of a new inspection: capture the vehicle's VIN.
 *
 * Two readers, one screen — the mode tabs exist because they are genuinely
 * different acts, not two rendering styles:
 *   • Disc  — points at a licence disc's PDF417 barcode and decodes continuously,
 *             no shutter. A digital read, so it is trusted outright.
 *   • Plate — photographs the stamped VIN plate and OCRs it. Needs a shutter
 *             (the user has to hold still) and always asks for confirmation.
 * Ported from Link/apps/turbo-mobile's LicenceDiscScanner; extraction logic
 * lives in ~/lib/vin so it is unit-tested independently of the camera.
 */

type Mode = 'disc' | 'plate';

/** A hand torch, from the design system's 16-grid set. */
const TORCH = 'M5 2h6l-1 4H6zM6 6v8h4V6M8 9v2';

/** The four reticle brackets, as corner offsets and which two edges each one draws. */
const CORNERS = [
  { key: 'tl', style: { left: 0, top: 0, borderLeftWidth: 3, borderTopWidth: 3 } },
  { key: 'tr', style: { right: 0, top: 0, borderRightWidth: 3, borderTopWidth: 3 } },
  { key: 'bl', style: { left: 0, bottom: 0, borderLeftWidth: 3, borderBottomWidth: 3 } },
  { key: 'br', style: { right: 0, bottom: 0, borderRightWidth: 3, borderBottomWidth: 3 } },
] as const;

export interface VinScanScreenProps {
  /** Called once the user has committed to a VIN. */
  onConfirm: (vin: string, source: VinCandidate['source']) => void;
  /** Skip is allowed: a VIN is an enrichment input, never a gate on assessment. */
  onSkip?: () => void;
  /** Leave the journey entirely. This route has no system header to go back from. */
  onClose?: () => void;
  /**
   * Whether the screen is the focused route. Supplied by the parent rather than
   * read here with useIsFocused so this component still renders outside a
   * navigator (tests, storybook). Defaults to focused.
   */
  isFocused?: boolean;
}

export function VinScanScreen({
  onConfirm,
  onSkip,
  onClose,
  isFocused = true,
}: VinScanScreenProps) {
  const { c, t, radius, space, touch } = useTheme();
  const [mode, setMode] = useState<Mode>('disc');
  const [candidate, setCandidate] = useState<VinCandidate | null>(null);
  const [busy, setBusy] = useState(false);
  const [manual, setManual] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [torch, setTorch] = useState(false);

  const cameraRef = useRef<Camera>(null);
  const { hasPermission, requestPermission } = useCameraPermission();
  const device = useCameraDevice('back');

  // Permissive on the initial read, strict on transitions. AppState.currentState
  // is null or 'unknown' very early in an Android cold launch, so requiring
  // 'active' here would leave the camera off until the first change event — i.e.
  // a black preview on the screen the app opens to.
  const [appActive, setAppActive] = useState(
    AppState.currentState !== 'background' && AppState.currentState !== 'inactive',
  );

  // The camera is an EXCLUSIVE system resource. Left active while backgrounded,
  // this screen keeps camera 0 open and every other app — a video call, the
  // stock camera — is denied it, which surfaced live as a conflict against a
  // Teams call. It also drains the battery and can leave the capture session in
  // a state where takePhoto never resolves.
  useEffect(() => {
    const sub = AppState.addEventListener('change', (next) => {
      setAppActive(next === 'active');
    });
    return () => sub.remove();
  }, []);

  // Off while a candidate is on screen or the user is typing, too — a live
  // preview underneath a confirm sheet keeps firing detections and re-opening
  // a sheet the user is trying to dismiss.
  const cameraActive = hasPermission && appActive && isFocused && !candidate && manual === null;

  useEffect(() => {
    if (!hasPermission) void requestPermission();
  }, [hasPermission, requestPermission]);

  useEffect(() => {
    logger.log('[VinScan] state', {
      mode,
      hasPermission,
      hasDevice: !!device,
      appActive,
      isFocused,
      cameraActive,
    });
  }, [mode, hasPermission, device, appActive, isFocused, cameraActive]);

  // ── disc mode: continuous PDF417 ──────────────────────────────────────────
  const codeScanner = useCodeScanner({
    codeTypes: ['pdf-417'],
    onCodeScanned: (codes) => {
      if (candidate || mode !== 'disc') return;
      for (const code of codes) {
        const value = (code as { value?: string }).value;
        if (!value) continue;
        const found = vinFromLicenceDisc(value);
        if (found) {
          logger.log('[VinScan] disc VIN', found.vin);
          setCandidate(found);
          return;
        }
      }
      // A disc that scans but carries no VIN field is a real outcome, not a
      // failed scan — say so rather than leaving the user pointing at it.
      setError('That disc scanned but has no VIN on it. Try the VIN plate instead.');
    },
  });

  // ── plate mode: shutter → OCR ─────────────────────────────────────────────
  const capture = useCallback(async () => {
    // Logged at entry, not just on the outcome: both guards below are silent
    // returns, and a shutter that does nothing is indistinguishable from a
    // shutter that was never wired up without this line.
    logger.log('[VinScan] capture pressed', { hasCamera: !!cameraRef.current, busy });
    if (!cameraRef.current || busy) return;
    setBusy(true);
    setError(null);
    try {
      const photo = await cameraRef.current.takePhoto({});
      logger.log('[VinScan] photo', photo?.path?.slice(0, 60));
      const uri = /^(blob:|data:)/.test(photo.path) ? photo.path : `file://${photo.path}`;
      const result = await TextRecognition.recognize(uri);
      const found = extractVin(result.text ?? '');
      if (found) {
        logger.log('[VinScan] plate VIN', found.vin, found.source);
        setCandidate(found);
      } else {
        setError('No VIN found in that shot. Fill the frame with the plate and hold still.');
      }
    } catch (e) {
      logger.error('[VinScan] capture failed', e);
      setError('Could not read that image. Try again, or enter the VIN by hand.');
    } finally {
      setBusy(false);
    }
  }, [busy]);

  // The VIN itself is the design system's mono title: 20/32, slashed zero, wide tracking.
  // 17 characters that have to be compared against a stamped plate by eye — mono is not a
  // style choice here, it is what makes 0 and O, and 5 and S, different shapes.
  const vinText = { ...t.monoTitle, color: c.text };

  /*
    The confirm sheet is an OVERLAY on the camera screen, not a screen of its own.

    It used to replace the whole view with a scrim, which painted a flat slab where the
    viewfinder had been — so the operator lost sight of the thing they were being asked to
    confirm against. The frame keeps the camera behind it. The camera itself is still
    deactivated while a candidate is up (see `cameraActive`): a live preview under a sheet
    keeps firing detections and re-opening the sheet the user is trying to dismiss.
  */
  const confirmSheet = candidate ? (
    <View
      // Contained above the gesture bar and never over the status bar: this is a decision
      // about one value, not a new place in the app.
      style={{
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        backgroundColor: c.scrim,
        justifyContent: 'flex-end',
      }}
    >
      <View
        style={{
          backgroundColor: c.surface,
          borderTopLeftRadius: radius.sheet,
          borderTopRightRadius: radius.sheet,
          padding: space.md,
          paddingTop: space.lg,
          gap: space.md,
        }}
      >
        <View>
          <Text style={[t.monoEyebrow, { color: c.dim, textTransform: 'uppercase' }]}>
            {candidate.source === 'barcode'
              ? 'VIN · read from licence disc'
              : 'VIN · read from photo'}
          </Text>
          <Text style={vinText} accessibilityLabel={candidate.vin.split('').join(' ')}>
            {candidate.vin}
          </Text>
        </View>

        {candidate.checkDigitValid ? (
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.sm }}>
            <Glyph path={TICK} color={c.decisionRepair} />
            <Text style={[t.label, { color: c.decisionRepair }]}>Check digit matches</Text>
          </View>
        ) : (
          // Advisory only. Many non-North-American VINs fail this check
          // legitimately, so it is worded as "check it" and never blocks.
          <Notice title="Check digit doesn't match.">
            That's normal outside the US and Canada. Use it if it matches the plate on the car.
          </Notice>
        )}

        <PrimaryButton
          testID="vin-use"
          label="Use this VIN"
          onPress={() => onConfirm(candidate.vin, candidate.source)}
        />
        <View style={{ flexDirection: 'row', gap: space.sm }}>
          <View style={{ flex: 1 }}>
            <OutlineButton
              label="Edit by hand"
              onPress={() => {
                // Clearing the candidate is what actually shows the editor — the
                // confirm sheet is checked first and would otherwise keep
                // rendering over it.
                setManual(candidate.vin);
                setCandidate(null);
              }}
            />
          </View>
          <View style={{ flex: 1 }}>
            <OutlineButton label="Scan again" onPress={() => setCandidate(null)} />
          </View>
        </View>
      </View>
    </View>
  ) : null;

  // ── manual entry ──────────────────────────────────────────────────────────
  if (manual !== null) {
    const value = normalizeVin(manual);
    const complete = value.length === 17;
    return (
      <Screen>
        <View style={{ flex: 1, padding: space.md, justifyContent: 'flex-end' }}>
          <Card style={{ gap: space.md }}>
            <Text style={[t.h2, { color: c.text }]}>Enter the VIN</Text>
            <TextInput
              testID="vin-manual"
              style={[
                vinText,
                {
                  backgroundColor: c.surface,
                  borderWidth: 1,
                  borderColor: c.border2,
                  borderRadius: radius.md,
                  paddingHorizontal: space.md,
                  height: 56,
                },
              ]}
              value={value}
              onChangeText={(next) => setManual(normalizeVin(next))}
              autoCapitalize="characters"
              autoCorrect={false}
              spellCheck={false}
              maxLength={17}
              placeholder="17 characters"
              placeholderTextColor={c.dim2}
              accessibilityLabel="Vehicle identification number"
            />
            <Text style={[t.body, { color: c.dim }]}>{value.length} of 17</Text>
            <PrimaryButton
              testID="vin-use"
              label="Use this VIN"
              disabled={!complete}
              onPress={() => onConfirm(value, 'manual')}
            />
            <OutlineButton label="Back to scanning" onPress={() => setManual(null)} />
          </Card>
        </View>
      </Screen>
    );
  }

  // ── camera ────────────────────────────────────────────────────────────────
  return (
    // The VIN scanner is a camera, so it takes the same inverse surface as Shoot.
    <Screen style={{ backgroundColor: c.inverse }}>
      {/* The app is light; this screen is not. */}
      <StatusBar barStyle="light-content" />
      <View style={{ flex: 1, padding: space.md, gap: space.md }}>
        {/*
          Close, title, torch. The title says which act this is — the two modes read the
          same barcode-shaped thing, and the segmented control below says which.
        */}
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <Pressable
            testID="vin-close"
            accessibilityRole="button"
            accessibilityLabel="Leave this inspection"
            onPress={onClose}
            disabled={!onClose}
            style={{
              width: touch.min,
              height: touch.min,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Text style={[t.bodyLg, { fontWeight: '600', color: c.onInverse }]}>
              {onClose ? '✕' : ''}
            </Text>
          </Pressable>
          <Text
            style={[
              t.bodyLg,
              { fontWeight: '600', color: c.onInverse, flex: 1, textAlign: 'center' },
            ]}
          >
            Scan the VIN
          </Text>
          {/*
            Only when the device has one. A torch button with no lamp behind it is worse
            than no torch button, and the web shim has none.
          */}
          {device?.hasTorch ? (
            <Pressable
              testID="vin-torch"
              accessibilityRole="switch"
              accessibilityState={{ checked: torch }}
              accessibilityLabel={torch ? 'Turn the torch off' : 'Turn the torch on'}
              onPress={() => setTorch((v) => !v)}
              style={{
                width: touch.min,
                height: touch.min,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Glyph path={TORCH} color={torch ? c.brandOnInverse : c.onInverse} size={20} />
            </Pressable>
          ) : (
            <View style={{ width: touch.min }} />
          )}
        </View>

        <View
          accessibilityRole="tablist"
          style={{
            flexDirection: 'row',
            gap: space.xs,
            padding: space.xs,
            borderRadius: radius.md,
            backgroundColor: c.inverseRaise,
          }}
        >
          {(['disc', 'plate'] as Mode[]).map((m) => {
            const on = mode === m;
            return (
              <Pressable
                key={m}
                onPress={() => {
                  logger.log('[VinScan] mode ->', m);
                  setMode(m);
                  setError(null);
                }}
                accessibilityRole="tab"
                accessibilityState={{ selected: on }}
                style={{
                  flex: 1,
                  minHeight: touch.min,
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: radius.sm,
                  backgroundColor: on ? c.surface : 'transparent',
                }}
              >
                <Text style={[t.label, { color: on ? c.text : c.onInverse }]}>
                  {m === 'disc' ? 'Licence disc' : 'VIN plate'}
                </Text>
              </Pressable>
            );
          })}
        </View>

        <View
          style={{
            flex: 1,
            borderRadius: radius.lg,
            overflow: 'hidden',
            backgroundColor: c.inverseRaise,
            justifyContent: 'center',
          }}
        >
          {hasPermission && device ? (
            <Camera
              ref={cameraRef}
              style={StyleSheet.absoluteFill}
              device={device}
              isActive={cameraActive}
              torch={torch ? 'on' : 'off'}
              photo
              onError={(e) => {
                logger.error('[VinScan] camera error', e.code, e.message);
                setError(
                  e.code === 'device/camera-already-in-use'
                    ? 'Another app is using the camera. Close it and try again.'
                    : `Camera problem (${e.code}). Try again, or enter the VIN by hand.`,
                );
              }}
              // Passing the config only in disc mode keeps the detector off
              // entirely while the user is lining up a plate shot.
              codeScanner={mode === 'disc' ? codeScanner : undefined}
            />
          ) : (
            <View style={{ alignItems: 'center', padding: space.lg, gap: space.md }}>
              <Text style={[t.body, { color: c.onInverse, textAlign: 'center' }]}>
                {hasPermission ? 'No camera found.' : 'Camera access is needed to scan a VIN.'}
              </Text>
              {!hasPermission ? (
                <PrimaryButton
                  label="Allow camera"
                  onPress={() => void requestPermission()}
                  onDark
                />
              ) : null}
            </View>
          )}
          {/*
            Corner brackets rather than a full rectangle at 55 % opacity. A translucent
            frame line disappears over a light licence disc, and the brackets say "fit it
            inside" without drawing a border across the thing being read.
          */}
          <View
            pointerEvents="none"
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              alignItems: 'center',
              justifyContent: 'center',
              gap: space.md,
            }}
          >
            <View style={{ width: 280, height: 144 }}>
              {CORNERS.map((corner) => (
                <View
                  key={corner.key}
                  style={{
                    position: 'absolute',
                    width: 32,
                    height: 32,
                    borderColor: c.onInverse,
                    ...corner.style,
                  }}
                />
              ))}
            </View>
            <Text style={[t.body, { color: c.onInverse, textAlign: 'center', maxWidth: 280 }]}>
              {mode === 'disc'
                ? 'Point at the barcode on the licence disc. It reads on its own.'
                : 'Fill the frame with the VIN plate, then tap to capture.'}
            </Text>
          </View>
        </View>

        {error ? <Notice tone="stateFailed">{error}</Notice> : null}

        {mode === 'plate' ? (
          <PrimaryButton
            testID="vin-capture"
            label="Capture VIN"
            busy={busy}
            disabled={!hasPermission}
            onPress={() => void capture()}
            onDark
          />
        ) : null}

        {/*
          The VIN is never a gate: it is an enrichment input, so both ways out sit at the
          same weight as each other and are visible without scrolling.
        */}
        <View style={{ flexDirection: 'row', gap: space.sm }}>
          <View style={{ flex: 1 }}>
            <OutlineButton label="Enter VIN by hand" onPress={() => setManual('')} onDark />
          </View>
          {onSkip ? (
            <View style={{ flex: 1 }}>
              <OutlineButton label="Skip for now" onPress={onSkip} onDark />
            </View>
          ) : null}
        </View>
      </View>
      {confirmSheet}
    </Screen>
  );
}
